import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { SessionGate } from '../features/auth/SessionGate';
import { AppShell } from '../features/shell/AppShell';
import { PLACEHOLDER_NAV } from '../features/shell/navigation';
import { SettingsNav } from '../features/members/SettingsNav';
import { RouteError } from '../features/shell/RouteError';
import { RouteSkeleton } from '../features/shell/RouteSkeleton';
import type { PlaceholderKey } from '../features/shell/PlaceholderPage';
import type { LegalPageKey } from '../features/legal/LegalPage';

const ProfilePage = lazy(() => import('../features/auth/ProfilePage').then((module) => ({ default: module.ProfilePage })));
const SignInPage = lazy(() => import('../features/auth/SignInPage').then((module) => ({ default: module.SignInPage })));
const SignUpPage = lazy(() => import('../features/auth/SignUpPage').then((module) => ({ default: module.SignUpPage })));
const SignUpConfirmPage = lazy(() =>
  import('../features/auth/SignUpConfirmPage').then((module) => ({ default: module.SignUpConfirmPage })),
);
const PasswordResetRequestPage = lazy(() =>
  import('../features/auth/PasswordResetRequestPage').then((module) => ({ default: module.PasswordResetRequestPage })),
);
const PasswordResetPage = lazy(() =>
  import('../features/auth/PasswordResetPage').then((module) => ({ default: module.PasswordResetPage })),
);
const InvitationPage = lazy(() =>
  import('../features/auth/InvitationPage').then((module) => ({ default: module.InvitationPage })),
);
const MembersPage = lazy(() => import('../features/members/MembersPage').then((module) => ({ default: module.MembersPage })));
const TenantSettingsPage = lazy(() =>
  import('../features/members/TenantSettingsPage').then((module) => ({ default: module.TenantSettingsPage })),
);
const AuditLogPage = lazy(() => import('../features/members/AuditLogPage').then((module) => ({ default: module.AuditLogPage })));
const IntegrationsPage = lazy(() =>
  import('../features/integrations/IntegrationsPage').then((module) => ({ default: module.IntegrationsPage })),
);
const ConsentPage = lazy(() => import('../features/integrations/ConsentPage').then((module) => ({ default: module.ConsentPage })));
const EntryPage = lazy(() => import('../features/knowledge/entries/EntryPage').then((module) => ({ default: module.EntryPage })));
const KnowledgePage = lazy(() => import('../features/knowledge/KnowledgePage').then((module) => ({ default: module.KnowledgePage })));
const MediaPage = lazy(() => import('../features/media/MediaPage').then((module) => ({ default: module.MediaPage })));
const MediaDetailPage = lazy(() => import('../features/media/MediaDetailPage').then((module) => ({ default: module.MediaDetailPage })));
const MediaSettingsPage = lazy(() => import('../features/media/MediaSettingsPage').then((module) => ({ default: module.MediaSettingsPage })));
const SettingsOverviewPage = lazy(() =>
  import('../features/members/SettingsOverviewPage').then((module) => ({ default: module.SettingsOverviewPage })),
);
const MorePage = lazy(() => import('../features/shell/MorePage').then((module) => ({ default: module.MorePage })));
const BillingPage = lazy(() => import('../features/billing/BillingPage').then((module) => ({ default: module.BillingPage })));
const AiUsagePage = lazy(() => import('../features/ai-usage/AiUsagePage').then((module) => ({ default: module.AiUsagePage })));
const AiSettingsPage = lazy(() => import('../features/ai-usage/AiSettingsPage').then((module) => ({ default: module.AiSettingsPage })));
const PublishingLanguagesPage = lazy(() =>
  import('../features/ai-usage/PublishingLanguagesPage').then((module) => ({ default: module.PublishingLanguagesPage })),
);
const BrandSettingsPage = lazy(() => import('../features/brand/BrandSettingsPage').then((module) => ({ default: module.BrandSettingsPage })));
const AccountsPage = lazy(() => import('../features/accounts/AccountsPage').then((module) => ({ default: module.AccountsPage })));
const PublicationDetailPage = lazy(() =>
  import('../features/publications/PublicationDetailPage').then((module) => ({ default: module.PublicationDetailPage })),
);
const AnalyticsOverviewPage = lazy(() => import('../features/analytics/AnalyticsOverviewPage').then((module) => ({ default: module.AnalyticsOverviewPage })));
const AnalyticsPostsPage = lazy(() => import('../features/analytics/AnalyticsPostsPage').then((module) => ({ default: module.AnalyticsPostsPage })));
const AnalyticsPostDetailPage = lazy(() =>
  import('../features/analytics/AnalyticsPostDetailPage').then((module) => ({ default: module.AnalyticsPostDetailPage })),
);
const PublicationHistoryPage = lazy(() =>
  import('../features/publications/PublicationHistoryPage').then((module) => ({ default: module.PublicationHistoryPage })),
);
const ConnectCandidatesPage = lazy(() =>
  import('../features/accounts/ConnectCandidatesPage').then((module) => ({ default: module.ConnectCandidatesPage })),
);
const CampaignsPage = lazy(() => import('../features/campaigns/CampaignsPage').then((module) => ({ default: module.CampaignsPage })));
const CampaignDetailPage = lazy(() =>
  import('../features/campaigns/CampaignDetailPage').then((module) => ({ default: module.CampaignDetailPage })),
);
const ContentPage = lazy(() => import('../features/content/ContentPage').then((module) => ({ default: module.ContentPage })));
const ContentEditorPage = lazy(() =>
  import('../features/content/ContentEditorPage').then((module) => ({ default: module.ContentEditorPage })),
);
const DesignEditorPage = lazy(() => import('../features/editor/DesignEditorPage').then((module) => ({ default: module.DesignEditorPage })));
const ReelEditorPage = lazy(() => import('../features/reels/ReelEditorPage').then((module) => ({ default: module.ReelEditorPage })));
const ReviewQueuePage = lazy(() => import('../features/review/ReviewQueuePage').then((module) => ({ default: module.ReviewQueuePage })));
const WeekApprovalPage = lazy(() => import('../features/review/WeekApprovalPage').then((module) => ({ default: module.WeekApprovalPage })));
const ReviewScreenPage = lazy(() => import('../features/review/ReviewScreenPage').then((module) => ({ default: module.ReviewScreenPage })));
const PropertiesSettingsPage = lazy(() =>
  import('../features/knowledge/PropertiesSettingsPage').then((module) => ({ default: module.PropertiesSettingsPage })),
);
const CalendarPage = lazy(() => import('../features/calendar/CalendarPage').then((module) => ({ default: module.CalendarPage })));
const NotificationsPage = lazy(() =>
  import('../features/notifications/NotificationsPage').then((module) => ({ default: module.NotificationsPage })),
);

const OccasionsPage = lazy(() => import('../features/planning/OccasionsPage').then((module) => ({ default: module.OccasionsPage })));
const PlanningPage = lazy(() => import('../features/planning/PlanningPage').then((module) => ({ default: module.PlanningPage })));
const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const HelpPage = lazy(() => import('../features/help/HelpPage').then((module) => ({ default: module.HelpPage })));
const HelpArticlePage = lazy(() => import('../features/help/HelpArticlePage').then((module) => ({ default: module.HelpArticlePage })));
const InboxPage = lazy(() => import('../features/inbox/InboxPage').then((module) => ({ default: module.InboxPage })));
const SetupPage = lazy(() => import('../features/setup/SetupPage').then((module) => ({ default: module.SetupPage })));
const WebsiteSetupPage = lazy(() => import('../features/setup/WebsiteSetupPage').then((module) => ({ default: module.WebsiteSetupPage })));
const SearchPage = lazy(() => import('../features/search/SearchPage').then((module) => ({ default: module.SearchPage })));

/** One chunk per area: later stages replace the placeholder with the feature. */
const PlaceholderPage = lazy(() =>
  import('../features/shell/PlaceholderPage').then((module) => ({ default: module.PlaceholderPage })),
);
const LandingPage = lazy(() => import('../features/landing/LandingPage').then((module) => ({ default: module.LandingPage })));
const LegalPage = lazy(() => import('../features/legal/LegalPage').then((module) => ({ default: module.LegalPage })));

function placeholder(area: PlaceholderKey): ComponentType {
  return function AreaPlaceholder() {
    return (
      <Suspense fallback={<RouteSkeleton />}>
        <PlaceholderPage area={area} />
      </Suspense>
    );
  };
}

/**
 * Public legal pages (spec 05 §9 Q2, style guide 2.38): reachable without a
 * session, so they stay outside SessionGate/AppShell like sign-in.
 */
function legalPage(page: LegalPageKey): ComponentType {
  return function LegalRoute() {
    return (
      <Suspense fallback={<RouteSkeleton />}>
        <LegalPage page={page} />
      </Suspense>
    );
  };
}

/**
 * Settings area (spec 10 §6, S10-7): each page renders the shared settings
 * sub-navigation below its heading. The sidebar entry `/settings` opens the
 * overview of every settings area (S150-7).
 */
function settingsArea(Page: ComponentType): ComponentType {
  return function SettingsArea() {
    return (
      <Suspense fallback={<RouteSkeleton />}>
        <Page />
      </Suspense>
    );
  };
}

function SignedInShell() {
  return (
    <SessionGate guest={<LandingRoute />}>
      <AppShell />
    </SessionGate>
  );
}

/** A visitor without a session at `/` sees the product page instead of the sign-in redirect (E380). */
function LandingRoute() {
  return (
    <Suspense fallback={<RouteSkeleton />}>
      <LandingPage />
    </Suspense>
  );
}

export const routes: RouteObject[] = [
  {
    // OAuth consent (spec 130 section 6): outside the app shell like sign-in, but behind the session gate,
    // which sends a guest to /sign-in and back to this exact URL.
    path: '/connect/consent',
    element: (
      <SessionGate>
        <Suspense fallback={<RouteSkeleton />}>
          <ConsentPage />
        </Suspense>
      </SessionGate>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/sign-in',
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <SignInPage />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/sign-up',
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <SignUpPage />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/sign-up/confirm/:token',
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <SignUpConfirmPage />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/password-reset',
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <PasswordResetRequestPage />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/password-reset/:token',
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <PasswordResetPage />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/invitations/:token',
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <InvitationPage />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  { path: '/legal/privacy', Component: legalPage('privacy'), errorElement: <RouteError /> },
  { path: '/legal/terms', Component: legalPage('terms'), errorElement: <RouteError /> },
  { path: '/legal/data-deletion', Component: legalPage('dataDeletion'), errorElement: <RouteError /> },
  {
    path: '/',
    Component: SignedInShell,
    errorElement: <RouteError />,
    children: [
      { index: true, Component: settingsArea(DashboardPage), errorElement: <RouteError /> },
      ...PLACEHOLDER_NAV.map(({ key, path }) => ({ path: path.slice(1), Component: placeholder(key), errorElement: <RouteError /> })),
      { path: 'setup', Component: settingsArea(SetupPage), errorElement: <RouteError /> },
      { path: 'setup/website', Component: settingsArea(WebsiteSetupPage), errorElement: <RouteError /> },
      { path: 'calendar', Component: settingsArea(CalendarPage), errorElement: <RouteError /> },
      // One route for both paths, so switching tabs never remounts the page (focus stays on the tab).
      { path: 'knowledge/:tab?', Component: settingsArea(KnowledgePage), errorElement: <RouteError /> },
      { path: 'knowledge/entries/:id', Component: settingsArea(EntryPage), errorElement: <RouteError /> },
      { path: 'content', Component: settingsArea(ContentPage), errorElement: <RouteError /> },
      // One route for both paths, so switching editor tabs never remounts the page.
      { path: 'content/:id/:tab?', Component: settingsArea(ContentEditorPage), errorElement: <RouteError /> },
      { path: 'content/:id/variants/:variantId/design', Component: settingsArea(DesignEditorPage), errorElement: <RouteError /> },
      { path: 'content/:id/variants/:variantId/reel', Component: settingsArea(ReelEditorPage), errorElement: <RouteError /> },
      { path: 'review', Component: settingsArea(ReviewQueuePage), errorElement: <RouteError /> },
      { path: 'review/week', Component: settingsArea(WeekApprovalPage), errorElement: <RouteError /> },
      { path: 'review/:id', Component: settingsArea(ReviewScreenPage), errorElement: <RouteError /> },
      { path: 'notifications', Component: settingsArea(NotificationsPage), errorElement: <RouteError /> },
      { path: 'search', Component: settingsArea(SearchPage), errorElement: <RouteError /> },
      { path: 'inbox', Component: settingsArea(InboxPage), errorElement: <RouteError /> },
      { path: 'help', Component: settingsArea(HelpPage), errorElement: <RouteError /> },
      { path: 'more', Component: settingsArea(MorePage), errorElement: <RouteError /> },
      { path: 'profile', Component: settingsArea(ProfilePage), errorElement: <RouteError /> },
      { path: 'help/:slug', Component: settingsArea(HelpArticlePage), errorElement: <RouteError /> },
      { path: 'planning', element: <Navigate to="/planning/year" replace /> },
      { path: 'planning/year', Component: settingsArea(PlanningPage), errorElement: <RouteError /> },
      { path: 'planning/occasions', Component: settingsArea(OccasionsPage), errorElement: <RouteError /> },
      { path: 'campaigns', Component: settingsArea(CampaignsPage), errorElement: <RouteError /> },
      { path: 'campaigns/:id', Component: settingsArea(CampaignDetailPage), errorElement: <RouteError /> },
      { path: 'media', Component: settingsArea(MediaPage), errorElement: <RouteError /> },
      { path: 'media/:id', Component: settingsArea(MediaDetailPage), errorElement: <RouteError /> },
      { path: 'settings', Component: settingsArea(SettingsOverviewPage), errorElement: <RouteError /> },
      { path: 'settings/members', Component: settingsArea(MembersPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'settings/properties', Component: settingsArea(PropertiesSettingsPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'settings/general', Component: settingsArea(TenantSettingsPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'settings/media', Component: settingsArea(MediaSettingsPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'settings/brand', Component: settingsArea(BrandSettingsPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'settings/accounts', Component: settingsArea(AccountsPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'publications', Component: settingsArea(PublicationHistoryPage), errorElement: <RouteError /> },
      { path: 'publications/:id', Component: settingsArea(PublicationDetailPage), errorElement: <RouteError /> },
      { path: 'analytics', Component: settingsArea(AnalyticsOverviewPage), errorElement: <RouteError /> },
      { path: 'analytics/posts', Component: settingsArea(AnalyticsPostsPage), errorElement: <RouteError /> },
      { path: 'analytics/posts/:publicationId', Component: settingsArea(AnalyticsPostDetailPage), errorElement: <RouteError /> },
      { path: 'settings/accounts/connect/:sessionId', Component: settingsArea(ConnectCandidatesPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'settings/ai', Component: settingsArea(AiSettingsPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'settings/ai/usage', Component: settingsArea(AiUsagePage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'settings/billing', Component: settingsArea(BillingPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'settings/languages', Component: settingsArea(PublishingLanguagesPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'settings/integrations', Component: settingsArea(IntegrationsPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: 'settings/audit-log', Component: settingsArea(AuditLogPage), errorElement: <RouteError nav={<SettingsNav />} /> },
      { path: '*', Component: placeholder('notFound') },
    ],
  },
];
